import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Button,
  TextField,
  IconButton,
  Card,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { api, firebase } from "common";
import { onValue, push, remove } from "firebase/database";
import { FirebaseConfig } from "../config/FirebaseConfig";

const icons = {
  'paypal': require('../assets/payment-icons/paypal-logo.png').default,
  'braintree': require('../assets/payment-icons/braintree-logo.png').default,
  'stripe': require('../assets/payment-icons/stripe-logo.png').default,
  'paytm': require('../assets/payment-icons/paytm-logo.png').default,
  'payulatam': require('../assets/payment-icons/payulatam-logo.png').default,
  'flutterwave': require('../assets/payment-icons/flutterwave-logo.png').default,
  'paystack': require('../assets/payment-icons/paystack-logo.png').default,
  'securepay': require('../assets/payment-icons/securepay-logo.png').default,
  'payfast': require('../assets/payment-icons/payfast-logo.png').default,
  'liqpay': require('../assets/payment-icons/liqpay-logo.png').default,
  'culqi': require('../assets/payment-icons/culqi-logo.png').default,
  'mercadopago': require('../assets/payment-icons/mercadopago-logo.png').default,
  'squareup': require('../assets/payment-icons/squareup-logo.png').default,
  'wipay': require('../assets/payment-icons/wipay-logo.png').default,
  'test': require('../assets/payment-icons/test-logo.png').default,
  'razorpay': require('../assets/payment-icons/razorpay-logo.png').default,
  'paymongo': require('../assets/payment-icons/paymongo-logo.png').default,
  'skipcash': require('../assets/payment-icons/skipcash-logo.png').default,
};

const MastercardLogo = () => (
  <div style={{ display: 'flex', position: 'relative', width: 40, height: 24 }}>
    <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#eb001b', opacity: 0.9 }} />
    <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#f79e1b', opacity: 0.9, marginLeft: -12 }} />
  </div>
);

const VisaLogo = () => (
  <span style={{ fontSize: 20, fontWeight: 'bold', fontStyle: 'italic', color: '#fff', fontFamily: 'sans-serif', letterSpacing: '0.5px' }}>
    VISA
  </span>
);

const getPaymentActionUrl = (link) => {
  if (!link) return "";
  if (link.startsWith("http://") || link.startsWith("https://")) {
    return link;
  }
  const serverUrl = `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net`;
  return `${serverUrl}${link}`;
};

export default function PaymentGatewayModal({
  open,
  onClose,
  orderId,
  amount,
  currency,
  productName,
  booking
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const auth = useSelector((state) => state.auth);
  const uid = auth.profile && auth.profile.uid ? auth.profile.uid : null;
  const settings = useSelector((state) => state.settingsdata.settings);
  let providers = useSelector((state) => state.paymentmethods.providers);
  if (providers && !Array.isArray(providers)) {
    let arr = [];
    let pms = Object.keys(providers);
    for (let i = 0; i < pms.length; i++) {
      if (providers[pms[i]].active) {
        arr.push({
          name: pms[i],
          link: '/' + pms[i] + '-link',
          ...providers[pms[i]]
        });
      }
    }
    providers = arr;
  }

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [savedCardsLoading, setSavedCardsLoading] = useState(false);
  const [addingCard, setAddingCard] = useState(false);

  // Form Fields
  const [cardHolder, setCardHolder] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [saveCard, setSaveCard] = useState(true);
  const [validationError, setValidationError] = useState("");

  const formRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset state on close
      setSelectedProvider(null);
      setAddingCard(false);
      setCardHolder("");
      setCardNumber("");
      setExpiryDate("");
      setCvv("");
      setValidationError("");
    }
  }, [open]);

  useEffect(() => {
    if (!uid || !open) return;
    setSavedCardsLoading(true);
    const cardsDbRef = firebase.savedCardsRef(uid);
    const unsubscribe = onValue(
      cardsDbRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const arr = Object.keys(data).map((key) => ({
            id: key,
            card: data[key].card,
          }));
          setSavedCards(arr);
        } else {
          setSavedCards([]);
        }
        setSavedCardsLoading(false);
      },
      (error) => {
        setSavedCards([]);
        setSavedCardsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [uid, open]);

  const isCardGateway = (provider) => {
    return false;
  };

  const handleProviderSelect = (provider) => {
    if (isCardGateway(provider)) {
      setSelectedProvider(provider);
      setAddingCard(false);
    } else {
      // Set the provider, which mounts the form. The useEffect below will handle submitting it.
      setSelectedProvider(provider);
    }
  };

  // Submit the payment form as soon as selectedProvider is set and the form is rendered/mounted in the DOM
  useEffect(() => {
    if (selectedProvider && !isCardGateway(selectedProvider) && formRef.current) {
      formRef.current.submit();
    }
  }, [selectedProvider]);

  const handleAddCardSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    const cleanCardNumber = cardNumber.replace(/\s/g, "");
    const cleanExpiry = expiryDate.replace(/\s/g, "");

    if (!cardHolder.trim()) {
      setValidationError(t("enter_cardholder_name") || "Please enter cardholder name");
      return;
    }
    if (cleanCardNumber.length < 13 || cleanCardNumber.length > 19) {
      setValidationError(t("enter_valid_card_number") || "Please enter a valid card number");
      return;
    }
    if (!/^\d{2}\/\d{2}$/.test(cleanExpiry)) {
      setValidationError(t("enter_valid_expiry") || "Please enter expiry date as MM/YY");
      return;
    }
    if (cvv.length < 3 || cvv.length > 4) {
      setValidationError(t("enter_valid_cvv") || "Please enter a valid CVV");
      return;
    }

    const month = parseInt(cleanExpiry.split("/")[0]);
    const year = parseInt(cleanExpiry.split("/")[1]);

    if (month < 1 || month > 12) {
      setValidationError(t("enter_valid_expiry_month") || "Invalid expiry month");
      return;
    }

    const isVisa = cleanCardNumber.startsWith("4");
    const isMaster = cleanCardNumber.startsWith("5");

    const newCard = {
      card: {
        network: isVisa ? "Visa" : isMaster ? "Mastercard" : "Other",
        last4: cleanCardNumber.slice(-4),
        name: cardHolder,
        expiry_month: String(month).padStart(2, "0"),
        expiry_year: "20" + year,
      },
    };

    try {
      if (saveCard && uid) {
        await push(firebase.savedCardsRef(uid), newCard);
      }
      // Submit the payment form directly
      setTimeout(() => {
        if (formRef.current) {
          formRef.current.submit();
        }
      }, 100);
    } catch (error) {
      setValidationError(t("failed_to_save_card") || "Failed to save card");
    }
  };

  const handleDeleteCard = async (e, cardId) => {
    e.stopPropagation();
    if (window.confirm(t("delete_card_confirm") || "Are you sure you want to delete this card?")) {
      try {
        if (uid && cardId) {
          await remove(firebase.savedCardEditRef(uid, cardId));
        }
      } catch (error) {
        alert(t("failed_to_delete_card") || "Failed to delete card");
      }
    }
  };

  const handleCardClick = () => {
    // Proceed with the selected card and submit payment form
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length > 0) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (v.length >= 2) {
      return v.substring(0, 2) + "/" + v.substring(2, 4);
    }
    return v;
  };

  const custId = booking ? (booking.customer || booking.cust_id) : uid;
  const mobileNo = booking ? (booking.customer_contact || booking.mobile_no) : (auth.profile ? auth.profile.mobile : "");
  const email = booking ? (booking.customer_email || booking.email) : (auth.profile ? auth.profile.email : "");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          backgroundColor: "var(--bg-card)",
          color: "var(--text-main)",
          border: "var(--card-border)",
          borderRadius: "16px",
          boxShadow: "var(--shadow)",
          padding: "16px",
        },
      }}
    >
      {/* Hidden Payment Submit Form */}
      {selectedProvider && (
        <form
          ref={formRef}
          action={getPaymentActionUrl(selectedProvider.link)}
          method="POST"
          style={{ display: "none" }}
        >
          <input type="hidden" name="order_id" value={orderId} />
          <input type="hidden" name="amount" value={amount} />
          <input type="hidden" name="currency" value={currency || settings?.code} />
          <input type="hidden" name="product_name" value={productName} />
          <input type="hidden" name="first_name" value={auth.profile?.firstName || ""} />
          <input type="hidden" name="last_name" value={auth.profile?.lastName || ""} />
          <input type="hidden" name="quantity" value={1} />
          <input type="hidden" name="cust_id" value={custId || ""} />
          <input type="hidden" name="mobile_no" value={mobileNo || ""} />
          <input type="hidden" name="email" value={email || ""} />
          <input type="hidden" name="web_url" value={window.location.origin} />
        </form>
      )}

      {/* Modal Header */}
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 16px",
        }}
      >
        <Box display="flex" alignItems="center">
          {selectedProvider && addingCard && (
            <IconButton
              onClick={() => {
                if (addingCard) {
                  setAddingCard(false);
                } else {
                  setSelectedProvider(null);
                }
              }}
              style={{ color: "var(--text-main)", marginRight: "8px" }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            {selectedProvider
              ? t("processing_payment") || "Processing Payment..."
              : t("select_payment_gateway") || t("select_payment_getway") || "Select Payment Gateway"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} style={{ color: "var(--text-main)" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers style={{ borderColor: "var(--border-color)", padding: "16px 24px" }}>
        {/* VIEW 1: Gateway Selection Grid */}
        {!selectedProvider && (
          <Box>
            <Typography
              variant="body2"
              style={{ color: "var(--text-secondary)", marginBottom: "24px" }}
            >
              {t("choose_gateway_desc") ||
                "Choose from the available secure gateways below to proceed with your transaction."}
            </Typography>

            <Grid container spacing={2}>
              {providers &&
                providers.map((provider) => (
                  <Grid item xs={6} sm={4} key={provider.name}>
                    <Card
                      onClick={() => handleProviderSelect(provider)}
                      style={{
                        backgroundColor: "var(--bg-main)",
                        border: "var(--card-border)",
                        borderRadius: "12px",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100px",
                        transition: "all 0.3s ease",
                        boxShadow: "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "var(--primary)";
                        e.currentTarget.style.transform = "translateY(-4px)";
                        e.currentTarget.style.boxShadow = "var(--shadow-neon)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "var(--border-color)";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <img
                        src={icons[provider.name]}
                        alt={provider.name}
                        style={{
                          maxHeight: "36px",
                          maxWidth: "80%",
                          objectFit: "contain",
                        }}
                      />
                      <Typography
                        variant="caption"
                        style={{
                          marginTop: "8px",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          color: "var(--text-main)",
                          fontSize: "10px",
                          letterSpacing: "1px",
                        }}
                      >
                        {provider.name}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}

        {selectedProvider && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" padding="40px" gap="16px">
            <CircularProgress style={{ color: "var(--primary)" }} />
            <Typography variant="body1" style={{ color: "var(--text-main)", fontWeight: "600", fontFamily: "Outfit, sans-serif" }}>
              {t("redirecting_payment", "Redirecting to secure payment page...")}
            </Typography>
            <Typography variant="body2" style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
              Please do not close this window or refresh the page.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "8px 16px" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          style={{
            borderColor: "var(--border-color)",
            color: "var(--text-main)",
            textTransform: "none",
            borderRadius: "8px",
            padding: "6px 16px",
          }}
        >
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
